// ---------------------------------------------------------------------------
// API client — thin fetch wrapper for the Survey Engine REST API
// ---------------------------------------------------------------------------
// Authorization: every request pulls the live Cognito ID token from
// Amplify's session each call, so token rotation is transparent. The
// API Gateway authorizer validates the ID token (not the access token).
//
// 401 handling: on receipt we sign the user out via Amplify and let the
// caller propagate the error — the AuthGuard will bounce them to /login
// on the next render. This catches refresh-token expiry and "user
// disabled" scenarios that the SPA otherwise wouldn't notice until the
// next page load.

import { fetchAuthSession, signOut } from 'aws-amplify/auth';
import { getApiBaseUrl } from '@/lib/config';

export interface ApiError {
  status: number;
  message: string;
  code?: string;
}

class ApiRequestError extends Error {
  status: number;
  code?: string;

  constructor(status: number, message: string, code?: string) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
  }
}

/** Pull the current Cognito ID token. Returns undefined when there's
 * no signed-in user (sign-in screen, or expired session). */
async function getAuthHeader(): Promise<Record<string, string>> {
  try {
    const session = await fetchAuthSession();
    const token = session.tokens?.idToken?.toString();
    return token ? { Authorization: `Bearer ${token}` } : {};
  } catch {
    return {};
  }
}

/** Generate a correlation ID for request tracing */
function correlationId(): string {
  return crypto.randomUUID();
}

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const authHeader = await getAuthHeader();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Correlation-Id': correlationId(),
    ...authHeader,
    ...((options.headers as Record<string, string>) ?? {}),
  };

  const response = await fetch(`${getApiBaseUrl()}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    // 401 means the JWT was rejected — either expired or the user is
    // no longer valid. Sign out so AuthGuard can bounce to /login.
    if (response.status === 401) {
      try {
        await signOut();
      } catch {
        // Already signed out, or Amplify isn't configured. Either way,
        // the error below propagates and the UI surface handles it.
      }
    }

    let message = `HTTP ${response.status}`;
    let code: string | undefined;
    try {
      const body = await response.json();
      message = body.message ?? message;
      code = body.code;
    } catch {
      // Response body not JSON — use status text
    }
    throw new ApiRequestError(response.status, message, code);
  }

  // 204 No Content
  if (response.status === 204) {
    return undefined as T;
  }

  return response.json();
}

// -- Convenience methods --

export const api = {
  get: <T>(path: string) => request<T>(path),

  post: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'POST', body: JSON.stringify(body) }),

  put: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PUT', body: JSON.stringify(body) }),

  patch: <T>(path: string, body: unknown) =>
    request<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),

  delete: <T>(path: string, body?: unknown) =>
    request<T>(path, {
      method: 'DELETE',
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    }),
};

export { ApiRequestError };
